import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { trackLeadSubmit, trackSkoolClick } from "@/lib/tracking";

const SKOOL_URL = "https://www.skool.com/sircs-reprogramacion-mental-8281";
const WHATSAPP_URL = "https://whatsapp.com/channel/0029Vb7YjesIiRp1ZrnRuP1M";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function FinalCTA() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Email no válido");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "landing-footer" });
      trackLeadSubmit(email);
      toast.success("Conexión establecida. Te enviaremos la guía a tu email.");
      setEmail("");
    } catch (err) {
      toast.error("No pudimos guardar tu email. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cta"
      data-testid="finalcta-section"
      className="surface-light relative py-24 sm:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 35% at 50% 0%, rgba(201,168,106,0.14), transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-5xl mx-auto"
        >
          <div className="font-body text-[11px] uppercase tracking-[0.28em] text-warm mb-8">
            · último mensaje del sistema ·
          </div>
          <h2
            data-testid="finalcta-headline"
            className="font-display text-5xl sm:text-6xl lg:text-8xl font-normal tracking-[-0.02em] leading-[0.95] text-ink"
          >
            Puedes seguir
            <br />
            <em className="italic text-taupe">repitiéndote</em>
            <br />
            o reescribir el código.
          </h2>
        </motion.div>

        {/* Email capture */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-16 max-w-2xl mx-auto bg-cream-soft rounded-2xl border border-taupe/15 p-6 sm:p-8"
        >
          <form onSubmit={onSubmit} data-testid="lead-form">
            <label className="block font-body text-[11px] uppercase tracking-[0.24em] text-taupe mb-3">
              Déjanos tu email — te enviamos la guía gratuita «5 errores que repite tu mente cada día»
            </label>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                data-testid="lead-form-email"
                className="flex-1 bg-cream border border-taupe/30 rounded-full px-5 py-3 font-body text-sm text-ink placeholder:text-taupe/60 focus:outline-none focus:border-warm"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit"
                className="btn-dark inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-body text-[11px] uppercase tracking-[0.22em] disabled:opacity-50"
              >
                {loading ? "Enviando…" : "Solicitar acceso"}
                {!loading && <ArrowUpRight size={14} />}
              </button>
            </div>
            <p className="mt-3 font-body text-[11px] text-taupe">
              o entra directamente a la comunidad ↓
            </p>
          </form>
        </motion.div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={SKOOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSkoolClick("finalcta")}
            data-testid="finalcta-skool"
            className="group btn-dark inline-flex items-center justify-between gap-4 px-10 py-5 rounded-full font-body text-[12px] uppercase tracking-[0.22em]"
          >
            Comenzar prueba gratis en Skool
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="finalcta-whatsapp"
            className="group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full font-body text-[12px] uppercase tracking-[0.22em] border border-taupe/40 text-ink hover:border-warm hover:text-warm transition-all"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Canal gratuito de WhatsApp
          </a>
        </div>
        <p className="mt-4 text-center font-body text-[12px] text-taupe max-w-md mx-auto">
          Contenido gratuito y avisos de próximas masterclass, directo a tu WhatsApp.
        </p>
      </div>
    </section>
  );
}
